import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Row, Col } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import { Claim } from '@dataTypes/claimFeature.types'
import ClaimPostingDetails from '../ClaimPostingInfo/ClaimPostingDetails'
import ClaimMemoModal from '../ClaimMemoModal/ClaimMemoModal.component'
import './ClaimPostingInfo.css'
import StatusBadge, {
  StatusTypes,
} from '@components/common/StatusBadge/StatusBadge.component'
import { filteredClaimsData } from '@utils/claims.util'
import DataTreeGridComponent from '@components/common/DataTreeGridComponent/DataTreeGridComponent'
import {
  getClaimsByPaymentReferenceNumber,
  postTransaction,
} from '@services/api/tracking.api'
import { getTrackingCheckStatus } from '@utils/tracking.util'
import { ConfirmationModal } from '@components/common'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'

interface ClaimPostingInfoProps {
  isVisible: boolean
  closeModal: () => void
  referenceNumber: string
  trackingTransactionData: any
  onPostSuccess: () => void
}

const ClaimPostingInfo: React.FC<ClaimPostingInfoProps> = ({
  isVisible,
  closeModal,
  referenceNumber,
  trackingTransactionData,
  onPostSuccess,
}) => {
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false)
  const [trackingTransaction, setTrackingTransaction] = useState<any>(
    trackingTransactionData,
  )
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null)
  const [trackingClaims, setTrackingClaims] = useState<Claim[]>([])
  const [rerender, setRerender] = useState(0)
  const [showPostConfirmationModal, setShowPostConfirmationModal] =
    useState(false)
  const { showToast } = useNotificationToast()

  useEffect(() => {
    const fetchTrackingClaims = async (): Promise<void> => {
      const response = await getClaimsByPaymentReferenceNumber(referenceNumber)
      const claimsData = filteredClaimsData(
        response.data,
        undefined,
        undefined,
        undefined,
      )
      setTrackingClaims(claimsData)
    }

    fetchTrackingClaims()
  }, [referenceNumber])

  useEffect(() => {
    if (trackingClaims.length > 0) {
      setSelectedClaim(trackingClaims[0])
      setRerender((prev) => prev + 1)
    }
  }, [trackingClaims])

  const handleClaimClick = (selected: Claim): void => {
    setSelectedClaim(selected)
  }

  const gridData =
    selectedClaim?.procedures.map((procedure) => {
      return {
        procedure_code: procedure.procedureCode,
        procedure_date: procedure.procedureDate,
        clinic_fee: procedure.clinic_fee,
        allowed_amount: procedure.allowed_amount,
        write_off: procedure.write_off,
        estimated_insurance_pay: procedure.estimated_insurance_pay,
        estimated_patient_resp: procedure.estimated_patient_resp,
        approved_amount: procedure.amountPaid,
        deductible: 0,
        copay: 0,
      }
    }) || []

  const columnsProcedures = useMemo(() => {
    return [
      {
        field: 'space',
        headerText: '',
        width: '16px',
      },
      {
        field: 'procedure_code',
        headerText: 'Code',
        width: '80px',
        footer: {
          template: () => <div>Total</div>,
        },
      },
      {
        field: 'procedure_date',
        headerText: 'Procedure date',
        width: '130px',
      },
      {
        field: 'clinic_fee',
        headerText: 'Clinic fee',
        width: '110px',
        template: (props: any) => {
          if (props.clinic_fee !== null && props.clinic_fee !== undefined) {
            return <span>${Number(props.clinic_fee).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'allowed_amount',
        headerText: 'Allowed amt.',
        width: '110px',
        template: (props: any) => {
          if (
            props.allowed_amount !== null &&
            props.allowed_amount !== undefined
          ) {
            return <span>${Number(props.allowed_amount).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'write_off',
        headerText: 'Write-off',
        width: '110px',
        template: (props: any) => {
          if (props.write_off !== null && props.write_off !== undefined) {
            return <span>${Number(props.write_off).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'estimated_insurance_pay',
        headerText: 'Est. ins. pay',
        width: '110px',
        template: (props: any) => {
          if (
            props.estimated_insurance_pay !== null &&
            props.estimated_insurance_pay !== undefined
          ) {
            return (
              <span>${Number(props.estimated_insurance_pay).toFixed(2)}</span>
            )
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'approved_amount',
        headerText: 'Approved ins.',
        width: '115px',
        template: (props: any) => {
          if (
            props.approved_amount !== null &&
            props.approved_amount !== undefined
          ) {
            return <span>${Number(props.approved_amount).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'deductible',
        headerText: 'Deductible',
        width: '110px',
        template: (props: any) => {
          if (props.deductible !== null && props.deductible !== undefined) {
            return <span>${Number(props.deductible).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'copay',
        headerText: 'Copay',
        width: 'auto',
        template: (props: any) => {
          if (props.copay !== null && props.copay !== undefined) {
            return <span>${Number(props.copay).toFixed(2)}</span>
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
      {
        field: 'estimated_patient_resp',
        headerText: 'Patient res.',
        width: '110px',
        template: (props: any) => {
          if (
            props.estimated_patient_resp !== null &&
            props.estimated_patient_resp !== undefined
          ) {
            return (
              <span>${Number(props.estimated_patient_resp).toFixed(2)}</span>
            )
          }
          return <span>${Number(0).toFixed(2)}</span>
        },
        footer: {
          template: (props: any) => (
            <div>${Number(props.Custom).toFixed(2)}</div>
          ),
        },
      },
    ].map((column) => ({
      ...column,
    }))
  }, [selectedClaim?.procedures])

  const handleProceedPost = async (): Promise<void> => {
    setShowPostConfirmationModal(false)
    const response = await postTransaction(
      trackingTransaction.tracking_transaction_id,
    )
    if (response.statusCode === 200) {
      setTrackingTransaction({
        ...trackingTransaction,
        posted_date: response.data.transaction.posted_date,
      })
      showToast(
        createSuccessToast(
          'Transaction posted successfully',
          'Transaction has been posted successfully.',
        ),
      )
      onPostSuccess()
    } else {
      showToast(
        createFailedToast(
          'Transaction posting failed',
          'Transaction posting failed. Please try again later.',
        ),
      )
    }
  }

  return (
    <Modal
      show={isVisible}
      onHide={closeModal}
      dialogClassName="modal-xxl"
      backdrop="static"
      className="view-claim-info-modal"
    >
      <div style={{ position: 'relative' }}>
        {isMemoModalOpen && <div className="modal-dark-overlay"></div>}

        <Modal.Header closeButton>
          <Modal.Title>Transaction info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col>
              <div className="d-flex justify-content-end mb-2">
                <Button
                  size="md"
                  className="button-primary"
                  onClick={() => setShowPostConfirmationModal(true)}
                  disabled={
                    !(
                      trackingTransaction.posted_date === null &&
                      trackingTransaction.pending === false
                    )
                  }
                >
                  Post
                </Button>
              </div>
            </Col>
          </Row>
          <Row className="gx-2">
            {/* Claim History Section */}
            <Col
              xs="auto"
              style={{
                border: '1px solid var(--blue-200)',
                padding: '0',
                borderRadius: '8px',
              }}
            >
              <div className="view-claim-info-section">
                <div className="view-claim-info-header">
                  <div className="view-claim-info-header-title">Claim list</div>
                  <div className="view-claim-info-count">
                    {`${trackingClaims.length} ${trackingClaims.length === 1 ? 'Claim' : 'Claims'}`}
                  </div>
                </div>
                <div className="view-claim-info-list">
                  {trackingClaims.map((c) => (
                    <div
                      key={c.id}
                      className={`view-claim-info-item ${selectedClaim?.id === c.id ? 'active' : ''}`}
                      onClick={() => handleClaimClick(c)}
                    >
                      <div className="view-claim-info-item-content-left">
                        <div className="view-claim-info-item-id">
                          {c.providerClaimId}
                        </div>
                        <div className="view-claim-info-item-date">
                          {new Date(c.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="view-claim-info-item-content-right">
                        <div className="view-claim-info-item-amount">
                          ${Number(c.claimAmount).toFixed(2)}
                        </div>
                        <div className="view-claim-info-item-status">
                          <StatusBadge
                            status={c.generalStatus as unknown as StatusTypes}
                            type="claims"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Col>

            <Col key={rerender}>
              <div className="view-posting-grid-container grid-procedures-container">
                <ClaimPostingDetails
                  patientName={selectedClaim?.patientName || ''}
                  payerCarrier={selectedClaim?.insuranceCarrier || ''}
                  subscriber={selectedClaim?.subscriberName || ''}
                  provider={selectedClaim?.providerName || ''}
                  entityType={selectedClaim?.entityType || 'Individual'}
                  claimId={selectedClaim?.providerClaimId || ''}
                  claimStatus={
                    selectedClaim?.generalStatus as unknown as StatusTypes
                  }
                  submittedAmount={selectedClaim?.claimAmount || ''}
                  itemType={selectedClaim?.claimType || ''}
                  dateSent={
                    selectedClaim?.createdAt
                      ? new Date(selectedClaim.createdAt).toLocaleDateString()
                      : ''
                  }
                  checkNo={trackingTransaction?.reference_number || 'N/A'}
                  checkStatus={
                    trackingTransaction
                      ? getTrackingCheckStatus(trackingTransaction)
                      : 'N/A'
                  }
                  paidAmount={
                    '$' +
                      Math.abs(Number(trackingTransaction?.amount)).toFixed(
                        2,
                      ) || 'N/A'
                  }
                  modeOfPayment={trackingTransaction?.payment_method || 'N/A'}
                  paymentDate={
                    trackingTransaction?.date
                      ? new Date(trackingTransaction?.date).toLocaleDateString()
                      : 'N/A'
                  }
                />

                <DataTreeGridComponent
                  data={gridData}
                  columns={columnsProcedures}
                  idMapping="id"
                  parentIdMapping="parentId"
                  treeColumnIndex={1}
                  rowHeight={36}
                  allowPaging={true}
                  allowTextWrap={true}
                  pageSize={10}
                  hasFooter={true}
                  footerData={columnsProcedures
                    .filter((col) => col.footer)
                    .map((col) => ({
                      field: col.field,
                      template: col.footer?.template ?? (() => <></>),
                    }))}
                />
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button size="md" className="button-secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </div>

      {isMemoModalOpen && (
        <ClaimMemoModal
          isVisible={isMemoModalOpen}
          closeModal={() => setIsMemoModalOpen(false)}
          onRestoreAfterNoteOverlay={() => setIsMemoModalOpen(true)}
          claimId={selectedClaim?.providerClaimId || ''}
          readOnly={true}
          claim={selectedClaim || undefined}
        />
      )}

      <ConfirmationModal
        show={showPostConfirmationModal}
        onHide={() => setShowPostConfirmationModal(false)}
        title="Confirmation"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          <div className="confirmation-modal__body">
            <p className="confirmation-modal__title">Proceed with post?</p>
            <p className="confirmation-modal__message">
              Are you sure you want to proceed with the post?
            </p>
          </div>
        }
        footer={
          <div className="confirmation-modal__footer">
            <Button
              size="md"
              className="button-secondary"
              onClick={() => setShowPostConfirmationModal(false)}
            >
              Cancel
            </Button>
            <Button
              size="md"
              className="button-primary"
              onClick={handleProceedPost}
            >
              Proceed
            </Button>
          </div>
        }
      />
    </Modal>
  )
}

export default ClaimPostingInfo
